import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import {
  ArrowUpDown,
  AlertCircle,
  FileText,
  TriangleAlert,
} from 'lucide-react';
import {
  formatCurrency,
  formatDate,
  formatPercentage,
} from '@/utils/formatters';
import { useFundConvertibles } from '@/hooks/react-query/useFunds';
import {
  ConvertibleInstrumentFundDetail,
  ConvertibleStatus,
} from '@/types/captable';
import type { ConvertibleDetailLocationState } from '@/components/cap-table/ConvertibleDetailPage';
import { getConvertibleStatusBadge } from '@/components/cap-table/convertibleStatusConfig';
import { INSTRUMENT_FIELD_CONFIG } from '@/components/cap-table/instrumentFieldConfig';

interface FundConvertiblesTabProps {
  fundId: string;
}

type SortField =
  | 'maturity_date'
  | 'issue_date'
  | 'investment_amount'
  | 'portfolio_company_name';
type SortOrder = 'asc' | 'desc';

export function FundConvertiblesTab({ fundId }: FundConvertiblesTabProps) {
  const {
    data: convertibles,
    isLoading,
    error,
  } = useFundConvertibles(fundId, true);
  const [sortField, setSortField] = useState<SortField>('maturity_date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const openConvertible = (convertible: ConvertibleInstrumentFundDetail) => {
    const state: ConvertibleDetailLocationState = {
      returnTo: `${location.pathname}${location.search}`,
    };
    navigate(
      `/app/portfolio/${convertible.portfolio_company_id}/convertibles/${convertible.id}`,
      { state }
    );
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const renderMaturityCell = (convertible: ConvertibleInstrumentFundDetail) => {
    const isOverdue =
      convertible.status === ConvertibleStatus.RUNNING &&
      convertible.maturity_date != null &&
      convertible.maturity_date <= new Date().toISOString().slice(0, 10);
    return (
      <span className="flex items-center gap-1">
        {formatDate(convertible.maturity_date) || '-'}
        {isOverdue && (
          <TriangleAlert
            className="h-4 w-4 text-yellow-500"
            aria-label="Maturity reached"
          />
        )}
      </span>
    );
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field)
      return (
        <ArrowUpDown className="ml-2 h-4 w-4 opacity-30 group-hover:opacity-100" />
      );
    return <ArrowUpDown className="ml-2 h-4 w-4 text-primary" />;
  };

  if (isLoading) {
    return (
      <div className="space-y-4 animate-in fade-in duration-500">Loading</div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 border border-destructive/20 text-destructive flex flex-col items-center justify-center p-8 space-y-4">
        <AlertCircle className="h-8 w-8" />
        <p className="font-medium text-center">
          Failed to load convertibles. Please try again later.
        </p>
      </div>
    );
  }

  if (!convertibles || convertibles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 border rounded-md border-dashed border-muted-foreground/30 bg-muted/20">
        <FileText className="h-10 w-10 text-muted-foreground mb-4 opacity-50" />
        <h3 className="text-lg font-medium text-foreground">
          No Convertible Loans
        </h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-md text-center">
          This fund does not have any outstanding convertible loans across its
          portfolio companies.
        </p>
      </div>
    );
  }

  const sortedConvertibles = [...convertibles].sort((a, b) => {
    let comparison = 0;

    switch (sortField) {
      case 'maturity_date':
        // Handle nulls by pushing them to the bottom
        if (!a.maturity_date && !b.maturity_date) comparison = 0;
        else if (!a.maturity_date) comparison = 1;
        else if (!b.maturity_date) comparison = -1;
        else
          comparison =
            new Date(a.maturity_date).getTime() -
            new Date(b.maturity_date).getTime();
        break;
      case 'issue_date':
        comparison =
          new Date(a.issue_date).getTime() - new Date(b.issue_date).getTime();
        break;
      case 'investment_amount':
        comparison = (a.investment_amount ?? 0) - (b.investment_amount ?? 0);
        break;
      case 'portfolio_company_name':
        comparison = a.portfolio_company_name.localeCompare(
          b.portfolio_company_name
        );
        break;
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const todayStr = new Date().toISOString().slice(0, 10);
  const visibleConvertibles = showOverdueOnly
    ? sortedConvertibles.filter(
        (c) =>
          c.status === ConvertibleStatus.RUNNING &&
          c.maturity_date != null &&
          c.maturity_date <= todayStr
      )
    : sortedConvertibles;

  return (
    <div className="space-y-4 animate-in fade-in duration-500">
      <div className="flex justify-end">
        <Button
          variant={showOverdueOnly ? 'default' : 'outline'}
          size="sm"
          onClick={() => setShowOverdueOnly((v) => !v)}
        >
          <TriangleAlert className="h-4 w-4 mr-2" />
          Overdue only
        </Button>
      </div>
      <div className="rounded-md border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                <TableHead className="w-[180px]">
                  <Button
                    variant="ghost"
                    className="hover:bg-transparent -ml-4 group font-semibold text-muted-foreground"
                    onClick={() => handleSort('portfolio_company_name')}
                  >
                    Company
                    {getSortIcon('portfolio_company_name')}
                  </Button>
                </TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">
                  <Button
                    variant="ghost"
                    className="hover:bg-transparent -mr-4 group justify-end font-semibold text-muted-foreground w-full"
                    onClick={() => handleSort('investment_amount')}
                  >
                    Amount
                    {getSortIcon('investment_amount')}
                  </Button>
                </TableHead>
                <TableHead>Valuation Cap</TableHead>
                <TableHead>Interest</TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    className="hover:bg-transparent -ml-4 group font-semibold text-muted-foreground"
                    onClick={() => handleSort('issue_date')}
                  >
                    Issue Date
                    {getSortIcon('issue_date')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    className="hover:bg-transparent -ml-4 group font-semibold text-muted-foreground"
                    onClick={() => handleSort('maturity_date')}
                  >
                    Maturity Date
                    {getSortIcon('maturity_date')}
                  </Button>
                </TableHead>
                <TableHead>Runtime</TableHead>
                <TableHead className="text-right">NAV</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleConvertibles.map((convertible) => {
                const status = getConvertibleStatusBadge(convertible);
                const fieldsConfig =
                  INSTRUMENT_FIELD_CONFIG[convertible.instrument_type] ??
                  INSTRUMENT_FIELD_CONFIG['convertible_note'];

                return (
                  <TableRow
                    key={convertible.id}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => openConvertible(convertible)}
                  >
                    <TableCell className="font-medium">
                      {convertible.portfolio_company_name}
                    </TableCell>
                    <TableCell>
                      <span className="capitalize">
                        {convertible.instrument_type.replaceAll('_', ' ')}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(convertible.investment_amount, {
                        currency: convertible.currency_iso_code,
                      })}
                    </TableCell>
                    <TableCell>
                      {fieldsConfig.has('valuation_cap') &&
                      convertible.valuation_cap
                        ? formatCurrency(convertible.valuation_cap, {
                            currency: convertible.currency_iso_code,
                          })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {fieldsConfig.has('interest_rate') &&
                      convertible.interest_rate
                        ? formatPercentage(convertible.interest_rate)
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {formatDate(convertible.issue_date) || '-'}
                    </TableCell>
                    <TableCell>{renderMaturityCell(convertible)}</TableCell>
                    <TableCell>
                      {fieldsConfig.has('runtime') &&
                      convertible.runtime_days != null
                        ? `${convertible.runtime_days} d`
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {(fieldsConfig.has('nav_including_interest') ||
                        fieldsConfig.has('fair_value')) &&
                      convertible.nav_including_interest != null
                        ? formatCurrency(convertible.nav_including_interest, {
                            currency: convertible.currency_iso_code,
                          })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={status.variant}
                        className="whitespace-nowrap"
                      >
                        {status.label}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
